import React, { useEffect, useState } from 'react'
import BudgetUsesSelector from './Components/BudgetUsesSelector'
import ProgramEditingSubSection from './Components/ProgramEditingSubSection'
import ProgramOption from './Components/ProgramOption'
import MicromobilityLeasingTrips from 'features/Micromobility/Shared/components/MicromobilityLeasingTrips'
import FormattingUtils from 'shared/FormattingUtils'
import MicromobilityUtils from 'features/Micromobility/Shared/MicromobilityUtils'
import ProgramPriceCoverage from 'core-system/Program/ProgramPriceCoverage'
import MicromobilityOneTimePurchase from 'features/Micromobility/Shared/components/MicromobilityOneTimePurchase'
import { getAllEmployeeSegment } from '../Shared/helperFunctions'
import moment from 'moment'
import { Month } from 'shared/DateUtils'
import { Segment } from 'redux/employeePlatform/employeePlatformTypes'
import {
  micromobilityType,
  micromobilityActiveOptionsType,
  availableMicromobilityMerchant,
} from '../Shared/types'
import { Merchant } from 'redux/programs/programsTypes'
import { MICROMOBILITY_INITIAL_STATE } from './ProgramEditingSlice'

interface MicromobilityProgramEditingProps {
  micromobility: micromobilityType
  availableMerchants: availableMicromobilityMerchant
  getMicromobilityRecommendations: (segmentId: string) => void
  changeMicromobilityActiveMerchantIds: (
    type: micromobilityActiveOptionsType,
    value: string[]
  ) => void
  changeMicromobilityActiveOptions: (
    type: micromobilityActiveOptionsType,
    value: boolean
  ) => void
  selectedDate: Month
  isContinued: boolean
  allSegments: Segment[]
  changeMicromobilityBudget: (budget: number) => void
}

const MicromobilityProgramEditing = ({
  micromobility = MICROMOBILITY_INITIAL_STATE,
  availableMerchants,
  getMicromobilityRecommendations,
  changeMicromobilityActiveMerchantIds,
  changeMicromobilityActiveOptions,
  selectedDate,
  isContinued,
  allSegments,
  changeMicromobilityBudget,
}: MicromobilityProgramEditingProps) => {
  const leasingMerchantIds = availableMerchants?.leasing.map((m) => m.id) || []
  const sharedMerchantIds = availableMerchants?.shared.map((m) => m.id) || []
  const oneTimeMerchantIds = availableMerchants?.oneTime.map((m) => m.id) || []

  useEffect(() => {
    // If the program is continued, we don't need to add active merchant ids
    if (availableMerchants && !isContinued) {
      changeMicromobilityActiveMerchantIds('leasing', leasingMerchantIds)
      changeMicromobilityActiveMerchantIds('shared', sharedMerchantIds)
      changeMicromobilityActiveMerchantIds('oneTime', oneTimeMerchantIds)
    }
  }, [availableMerchants])

  useEffect(() => {
    if (allSegments.length > 0) {
      const allEmployeeSegment = getAllEmployeeSegment(allSegments)
      getMicromobilityRecommendations(allEmployeeSegment.id)
    }
  }, [allSegments])

  const leaseCoverage = Math.min(
    Math.round(
      ((micromobility.budget ? micromobility.budget : 0) /
        MicromobilityUtils.avgMonthlyLeasing) *
        100
    ),
    100
  )

  const monthsLeft = 13 - parseInt(moment(selectedDate?.iso).format('M'))

  const annualCoverage = Math.min(
    Math.round(
      (((micromobility.budget ? micromobility.budget : 0) * monthsLeft) /
        MicromobilityUtils.avgAnnualLeasing) *
        100
    ),
    100
  )

  const handleTypeToggle = (type: micromobilityActiveOptionsType) => {
    const newActive = !micromobility.activeOptions[type]
    changeMicromobilityActiveOptions(type, newActive)
  }

  const handleProviderChange = (
    type: micromobilityActiveOptionsType,
    newActiveMerchantIds: string[]
  ) => {
    changeMicromobilityActiveMerchantIds(type, newActiveMerchantIds)
  }

  return (
    <>
      <ProgramEditingSubSection>
        <BudgetUsesSelector
          budget={micromobility.budget}
          onBudgetChange={(budget) => changeMicromobilityBudget(budget * 100)}
          budgetSubtitle='Monthly Budget Per Commuter'
          showUses={false}
          budgetError={micromobility.budget <= 0}
        />
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        <ProgramOption
          type='shared'
          active={micromobility.activeOptions.shared}
          activeMerchantIds={micromobility.activeMerchantIds.shared}
          availableMerchantIds={sharedMerchantIds}
          handleToggle={() => handleTypeToggle('shared')}
          handleProviderChange={(newActiveMerchantIds) =>
            handleProviderChange('shared', newActiveMerchantIds)
          }
        >
          <MicromobilityLeasingTrips budget={0} />
        </ProgramOption>
      </ProgramEditingSubSection>
      <ProgramEditingSubSection>
        <ProgramOption
          type='leasing'
          active={micromobility.activeOptions.leasing}
          activeMerchantIds={micromobility.activeMerchantIds.leasing}
          availableMerchantIds={leasingMerchantIds}
          handleToggle={() => handleTypeToggle('leasing')}
          handleProviderChange={(newActiveMerchantIds) =>
            handleProviderChange('leasing', newActiveMerchantIds)
          }
        >
          <ProgramPriceCoverage
            percentage={leaseCoverage}
            title='Monthly Membership Price Covered'
            description={`Your budget will cover ${leaseCoverage}% of the average monthly
                membership price (${FormattingUtils.formatDollar(
                  MicromobilityUtils.avgMonthlyLeasing,
                  0
                )}) for your commuters.`}
          />
        </ProgramOption>
      </ProgramEditingSubSection>
      <ProgramEditingSubSection isLastSection>
        <ProgramOption
          type='oneTime'
          active={micromobility.activeOptions.oneTime}
          activeMerchantIds={micromobility.activeMerchantIds.oneTime}
          availableMerchantIds={oneTimeMerchantIds}
          handleToggle={() => handleTypeToggle('oneTime')}
          handleProviderChange={(newActiveMerchantIds) =>
            handleProviderChange('oneTime', newActiveMerchantIds)
          }
        >
          <MicromobilityOneTimePurchase
            percentage={annualCoverage}
            budget={micromobility.budget}
          />
        </ProgramOption>
      </ProgramEditingSubSection>
    </>
  )
}

export default React.memo(MicromobilityProgramEditing)
